import { useState, JSX } from 'react';
import { Stack, Group, Text, Badge, Button, Paper } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Patient, Encounter, AllergyIntolerance } from '@medplum/fhirtypes';
import { AllergyListItem, AllergyAction } from './AllergyListItem';
import { AllergyInputModal } from './AllergyInputModal';
import { useAllergyActions } from '@/hooks/useAllergyActions';
import { createNewAllergyIntolerance } from '@/utils/allergy.utils';

export interface AllergyListProps {
  allergies: AllergyIntolerance[];
  onChange: (allergies: AllergyIntolerance[]) => void;
  currentEncounterId?: string;
  patient: Patient;
  encounter: Encounter | undefined;
  readonly?: boolean;
}

export const AllergyList = ({
  allergies,
  onChange,
  patient,
  encounter,
  readonly = false,
}: AllergyListProps): JSX.Element => {
  const { t } = useTranslation(['patient-pages']);

  // Use the allergy actions hook for server operations
  const { saveAllergy, deleteAllergy } = useAllergyActions(patient, encounter);

  // Modal states
  const [allergyModalOpened, setAllergyModalOpened] = useState(false);
  const [editingAllergy, setEditingAllergy] = useState<
    AllergyIntolerance | undefined
  >();
  const [editingIndex, setEditingIndex] = useState<number | undefined>();
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [deletingIndex, setDeletingIndex] = useState<number | undefined>();

  // Handlers
  const handleAddAllergy = () => {
    // Create a new empty allergy with patient/encounter context
    if (!encounter) {
      notifications.show({
        color: 'red',
        title: t('summaryPage.summary.allergies.encounterRequired', {
          defaultValue: 'Encounter is required to add an allergy',
        }),
        message: t('summaryPage.summary.allergies.encounterRequiredMessage', {
          defaultValue: 'Please create an encounter first',
        }),
      });
      return;
    }

    const newAllergy = createNewAllergyIntolerance(patient, encounter);
    setEditingAllergy(newAllergy);
    setEditingIndex(undefined);
    setModalMode('add');
    setAllergyModalOpened(true);
  };

  const handleAction = async (index: number, action: AllergyAction) => {
    const allergy = allergies[index];

    switch (action) {
      case 'edit':
        setEditingAllergy(allergy);
        setEditingIndex(index);
        setModalMode('edit');
        setAllergyModalOpened(true);
        break;

      case 'remove': {
        // If the allergy has an ID, it's saved on server - soft delete it
        if (allergy.id) {
          setDeletingIndex(index);
          try {
            await deleteAllergy(allergy.id);
            // Remove from local state after successful server deletion
            const updated = allergies.filter((_, i) => i !== index);
            onChange(updated);
          } catch (error) {
            notifications.show({
              color: 'red',
              title: t('summaryPage.summary.allergies.deleteErrorTitle', {
                defaultValue: 'Failed to remove allergy',
              }),
              message: String(error),
            });
          } finally {
            setDeletingIndex(undefined);
          }
        } else {
          // Not saved yet - just remove from local state
          const updated = allergies.filter((_, i) => i !== index);
          onChange(updated);
        }
        break;
      }
    }
  };

  const handleSaveSuccess = (allergy: AllergyIntolerance) => {
    if (editingIndex !== undefined) {
      // Update existing
      const updated = allergies.map((a, i) =>
        i === editingIndex ? allergy : a
      );
      onChange(updated);
    } else {
      // Add new
      onChange([...allergies, allergy]);
    }
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group gap="xs">
        <Text fw={600} size="md">
          {t('summaryPage.summary.allergies.title', {
            ns: 'patient-pages',
          })}
        </Text>
        {allergies.length > 0 && (
          <Badge size="sm" variant="light">
            {allergies.length}
          </Badge>
        )}
      </Group>

      {/* Empty state */}
      {allergies.length === 0 ? (
        <Paper withBorder radius="md" p="xl" bg="gray.0">
          <Text size="sm" c="dimmed" ta="center">
            {t('summaryPage.summary.allergies.noAllergies', {
              ns: 'patient-pages',
              defaultValue: 'No allergies recorded',
            })}
          </Text>
        </Paper>
      ) : (
        <>
          {/* Allergies List */}
          <Stack gap="xs">
            {allergies.map((allergy) => {
              // Find the actual index in the full allergies array
              const actualIndex = allergies.findIndex(
                (a) => a.id === allergy.id || a === allergy
              );
              return (
                <AllergyListItem
                  key={allergy.id || `allergy-${actualIndex}`}
                  allergy={allergy}
                  onAction={(action) => handleAction(actualIndex, action)}
                  isDeleting={deletingIndex === actualIndex}
                  hideActions={readonly}
                />
              );
            })}
          </Stack>
        </>
      )}

      {/* Add Allergy Button */}
      {encounter && !readonly && (
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={handleAddAllergy}
        >
          {t('summaryPage.summary.allergies.addNew', {
            ns: 'patient-pages',
          })}
        </Button>
      )}

      {/* Modal */}
      {encounter && (
        <AllergyInputModal
          opened={allergyModalOpened}
          onClose={() => setAllergyModalOpened(false)}
          onSave={saveAllergy}
          onSaveSuccess={handleSaveSuccess}
          allergy={editingAllergy}
          mode={modalMode}
          patient={patient}
          encounter={encounter}
        />
      )}
    </Stack>
  );
};
